import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Alert,
  Spinner, Modal
} from 'react-bootstrap';
import axios from 'axios';
// Use regular react-helmet - will work with v6.1.0
import { Helmet } from 'react-helmet';
import { BASE_URL } from '../../services/api';

const Contact = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeContact, setActiveContact] = useState(null);
  
  // Contact form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    contact_location: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedMap, setSelectedMap] = useState('');

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/contact_info.php?format_phones=true`);
      
      if (response.data.success) {
        const activeContacts = response.data.data
          .filter(contact => contact.is_active === "1" || contact.is_active === 1)
          .sort((a, b) => a.display_order - b.display_order);
        
        setContacts(activeContacts);
        if (activeContacts.length > 0) {
          setActiveContact(activeContacts[0].id);
          setFormData(prev => ({ ...prev, contact_location: activeContacts[0].id }));
        }
      } else {
        setError('Failed to load contact information');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      // Prepare form data
      const contactName = contacts.find(c => c.id == formData.contact_location)?.name || 'General';
      
      const submissionData = {
        ...formData,
        contact_location: contactName
      };

      // Send data to backend API
      const response = await axios.post(
        `${API_BASE_URL}/contact_form.php`,
        submissionData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      // Check response from server
      if (response.data.success) {
        // Success - show message and reset form
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          contact_location: activeContact || ''
        });
        
        // Hide success message after 5 seconds
        setTimeout(() => setSubmitted(false), 5000);
        
        console.log('Form submitted successfully:', response.data);
      } else {
        // Server returned error
        setError(response.data.message || 'Failed to send message. Please try again.');
      }
      
    } catch (err) {
      console.error('Form submission error:', err);
      
      if (err.response) {
        const errorMsg = err.response.data?.message || `Server error: ${err.response.status}`;
        setError(errorMsg);
      } else if (err.request) {
        setError('No response from server. Please check your connection and try again.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactSelect = (contactId) => {
    setActiveContact(contactId);
    setFormData(prev => ({ ...prev, contact_location: contactId }));
  };

  const handleViewMap = (embedCode) => {
    let mapSrc = embedCode;
    if (embedCode.includes('<iframe')) {
      const srcMatch = embedCode.match(/src="([^"]+)"/);
      if (srcMatch && srcMatch[1]) {
        mapSrc = srcMatch[1];
      }
    }
    setSelectedMap(mapSrc);
    setShowMapModal(true);
  };

  const getSocialColor = (url) => {
    if (!url) return 'secondary';
    
    if (url.includes('facebook.com')) return 'primary';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'info';
    if (url.includes('linkedin.com')) return 'linkedin';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('youtube.com')) return 'danger';
    return 'secondary';
  };

  const formatFullAddress = (contact) => {
    const parts = [];
    if (contact.address_line1) parts.push(contact.address_line1);
    if (contact.address_line2) parts.push(contact.address_line2);
    if (contact.city && contact.state) {
      parts.push(`${contact.city}, ${contact.state} ${contact.postal_code || ''}`);
    } else if (contact.city) {
      parts.push(contact.city);
    }
    if (contact.country) parts.push(contact.country);
    return parts.join(', ');
  };

  const extractMapSrc = (embedCode) => {
    if (!embedCode) return null;
    
    if (embedCode.includes('<iframe')) {
      const srcMatch = embedCode.match(/src="([^"]+)"/);
      if (srcMatch && srcMatch[1]) {
        return srcMatch[1];
      }
    }
    return embedCode;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading contact information...</p>
      </Container>
    );
  }

  // Render contact details and form
  const renderContactContent = () => {
    if (contacts.length === 0) {
      return (
        <Alert variant="info" className="text-center">
          <i className="fas fa-info-circle me-2"></i>
          No contact information available at the moment.
        </Alert>
      );
    }

    const contact = contacts[0]; // Assuming single contact

    return (
      <>
        {/* Contact Details and Form in Single Row */}
        <Row className="mb-5">
          {/* Contact Details Column */}
          <Col lg={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-4">
                  <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                  Contact Information
                </Card.Title>
                
                <div className="mb-4">
                  <h4>{contact.name}</h4>
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">
                      <i className="fas fa-map-marker-alt me-2"></i>
                      Address
                    </h6>
                    <p className="mb-0">
                      {formatFullAddress(contact)}
                    </p>
                  </div>

                  <div className="mb-3">
                    <h6 className="text-muted mb-2">
                      <i className="fas fa-phone me-2"></i>
                      Contact Information
                    </h6>
                    {contact.phone_main && (
                      <p className="mb-1">
                        <strong>Phone:</strong> {contact.phone_main}
                      </p>
                    )}
                    {contact.phone_alternate && (
                      <p className="mb-1">
                        <strong>Alternate:</strong> {contact.phone_alternate}
                      </p>
                    )}
                    {contact.email_general && (
                      <p className="mb-1">
                        <strong>Email:</strong> {contact.email_general}
                      </p>
                    )}
                  </div>

                  {/* Hours */}
                  {(contact.mon_fri_hours || contact.saturday_hours || contact.sunday_hours) && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">
                        <i className="fas fa-clock me-2"></i>
                        Office Hours
                      </h6>
                      <ul className="list-unstyled">
                        {contact.mon_fri_hours && (
                          <li className="mb-2">
                            <strong>Mon - Fri:</strong> {contact.mon_fri_hours}
                          </li>
                        )}
                        {contact.saturday_hours && (
                          <li className="mb-2">
                            <strong>Saturday:</strong> {contact.saturday_hours}
                          </li>
                        )}
                        {contact.sunday_hours && (
                          <li className="mb-2">
                            <strong>Sunday:</strong> {contact.sunday_hours}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Website */}
                  {contact.website_url && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">
                        <i className="fas fa-globe me-2"></i>
                        Website
                      </h6>
                      <a 
                        href={contact.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        {contact.website_url}
                      </a>
                    </div>
                  )}

                  {/* Social Media */}
                  {(contact.facebook_url || contact.twitter_url || contact.linkedin_url || 
                    contact.instagram_url || contact.youtube_url) && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">
                        <i className="fas fa-share-alt me-2"></i>
                        Connect With Us
                      </h6>
                      <div className="d-flex gap-2">
                        {contact.facebook_url && (
                          <a 
                            href={contact.facebook_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.facebook_url)} btn-sm`}
                          >
                            <i className="fab fa-facebook-f"></i>
                          </a>
                        )}
                        {contact.twitter_url && (
                          <a 
                            href={contact.twitter_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.twitter_url)} btn-sm`}
                          >
                            <i className="fab fa-twitter"></i>
                          </a>
                        )}
                        {contact.linkedin_url && (
                          <a 
                            href={contact.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.linkedin_url)} btn-sm`}
                          >
                            <i className="fab fa-linkedin-in"></i>
                          </a>
                        )}
                        {contact.instagram_url && (
                          <a 
                            href={contact.instagram_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.instagram_url)} btn-sm`}
                          >
                            <i className="fab fa-instagram"></i>
                          </a>
                        )}
                        {contact.youtube_url && (
                          <a 
                            href={contact.youtube_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.youtube_url)} btn-sm`}
                          >
                            <i className="fab fa-youtube"></i>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contact Form Column */}
          <Col lg={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-4">
                  <i className="fas fa-paper-plane me-2 text-primary"></i>
                  Send us a Message
                </Card.Title>
                
                {error && (
                  <Alert variant="danger" className="mb-4">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </Alert>
                )}
                
                {submitted && (
                  <Alert variant="success" className="mb-4">
                    <i className="fas fa-check-circle me-2"></i>
                    Thank you for your message! We'll get back to you soon.
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your name"
                      disabled={submitting}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter your email"
                          disabled={submitting}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          disabled={submitting}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Subject *</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter subject"
                      disabled={submitting}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your message"
                      disabled={submitting}
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    className="w-100"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  <div className="mt-3 text-center text-muted small">
                    <i className="fas fa-info-circle me-1"></i>
                    We'll respond within 24-48 hours
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Large Map Section Below */}
        <Row className="mb-5">
          <Col lg={12}>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title className="mb-4">
                  <i className="fas fa-map me-2 text-primary"></i>
                  Our Location
                </Card.Title>
                
                <div className="mb-3">
                  <h5 className="mb-2">{contact.name}</h5>
                  <p className="text-muted mb-4">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    {formatFullAddress(contact)}
                  </p>
                </div>
                
                {/* Large Map Display */}
                {contact.google_maps_embed_code ? (
                  <div className="map-container" style={{ width: '100%', height: '500px', borderRadius: '8px', overflow: 'hidden' }}>
                    <iframe
                      src={extractMapSrc(contact.google_maps_embed_code)}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Maps Location"
                    />
                  </div>
                ) : contact.latitude && contact.longitude ? (
                  <div className="map-container" style={{ width: '100%', height: '500px', borderRadius: '8px', overflow: 'hidden' }}>
                    <iframe
                      src={`https://maps.google.com/maps?q=${contact.latitude},${contact.longitude}&z=15&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Maps Location"
                    />
                  </div>
                ) : (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-map-marked-alt me-2"></i>
                    Map location not available
                  </Alert>
                )}
                
                {/* Map Actions */}
                <div className="mt-3 d-flex justify-content-between">
                  <div>
                    {contact.latitude && contact.longitude && (
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Coordinates: {contact.latitude}, {contact.longitude}
                      </small>
                    )}
                  </div>
                  <div>
                    {contact.google_maps_embed_code && (
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleViewMap(contact.google_maps_embed_code)}
                      >
                        <i className="fas fa-external-link-alt me-2"></i>
                        Open Full Screen Map
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      {/* Using react-helmet for SEO - works with v6.1.0 */}
      <Helmet>
        <title>Contact Us | Veda Builders & Promoters</title>
        <meta name="description" content="Get in touch with Veda Builders & Promoters. Find our contact information, location, and send us a message." />
        <meta name="keywords" content="contact, builders, promoters, real estate, construction, inquiry" />
        <meta name="author" content="Veda Builders & Promoters" />
      </Helmet>

      <Container className="py-5">
        <h1 className="display-4 mb-4 text-center">Contact Us</h1>
        <p className="lead text-center mb-5">
          Get in touch with Veda Builders & Promoters for any queries or information
        </p>

        {renderContactContent()}
      </Container>

      {/* Map Modal */}
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Location Map</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMap ? (
            <div className="map-container" style={{ width: '100%', height: '500px' }}>
              <iframe
                src={selectedMap}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Maps Location"
              />
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-map fa-3x text-muted mb-3"></i>
              <p>Map not available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMapModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Contact;